import { SUFFIX_OPTIONS } from '@modules/ContactManager/components/Tabs/components/Contact/interfaces';
import { MILITARY_RANK_OPTIONS } from '@shared/constants/lists';
import { statesList } from '@shared/constants/statesList';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { SSNInputField } from '@shared/fields/SSNInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { ZipCodeInputField } from '@shared/fields/ZipCodeInputField';
import { ADDRESS_TYPE_OPTIONS, CONDITIONS_OPTIONS, EMPLOYMENT_TYPE_OPTIONS, MARITAL_STATUS_OPTIONS } from '@shared/lists';
import { ICreateContact } from '../queries';

const addressSharedConfig = (prefix: string) => [
  {
    width: '100%',
    name: `${prefix}.streetLine1`,
    label: 'Street',
  },
  {
    width: '50%',
    name: `${prefix}.unit`,
    label: 'Unit/Apt',
  },
  {
    width: '50%',
    name: `${prefix}.city`,
  },
  {
    width: '50%',
    name: `${prefix}.state`,
    options: statesList,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: `${prefix}.zip`,
    component: ZipCodeInputField,
  },
];

export const currentAddressConfig = (prefix: string) => [
  ...addressSharedConfig(prefix),
  {
    width: '100%',
    type: 'label',
    label: 'Time at Residence',
  },
  {
    width: '50%',
    name: `${prefix}.years`,
    label: 'Years',
    component: NumberInputField,
  },
  {
    width: '50%',
    name: `${prefix}.months`,
    label: 'Months',
    component: NumberInputField,
  },
  {
    width: '100%',
    type: 'label',
    label: 'Residence Type and Expense',
  },
  {
    width: '30%',
    name: `${prefix}.type`,
    label: 'Type',
    options: ADDRESS_TYPE_OPTIONS,
    component: SelectAutocompleteField,
  },
  {
    width: '35%',
    name: `${prefix}.monthlyPayment`,
    label: 'Monthly Payment',
    prefix: '$',
    decimalScale: 2,
    component: NumberInputField,
  },
  {
    width: '35%',
    name: `${prefix}.includeInDti`,
    label: 'DTI?',
    options: CONDITIONS_OPTIONS,
    component: SelectAutocompleteField,
  },
];

export const previousAddressConfig = (prefix: string) => [
  ...addressSharedConfig(prefix),
  {
    width: '100%',
    type: 'label',
    label: 'Time at Residence',
  },
  {
    width: '50%',
    name: `${prefix}.years`,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: `${prefix}.months`,
    component: NumberInputField,
  },
];

export const driversLicenseConfig = [
  {
    width: '100%',
    label: 'DL Number',
    name: 'driverLicenseNumber',
  },
  {
    width: '100%',
    label: 'DL State',
    name: 'driverLicenseState',
    options: statesList,
    component: SelectAutocompleteField,
  },
  {
    width: '100%',
    label: 'DL Issued Date',
    name: 'driverLicenseIssuesOnDate',
    component: SelectDateField,
  },
  {
    width: '100%',
    label: 'DL Expiration Date',
    name: 'driverLicenseExpireDate',
    component: SelectDateField,
  },
];

export const employerInfoConfig = (
  isCurrent = false,
  prefix: keyof Pick<ICreateContact, 'currentEmployer' | 'previousEmployer'>,
  onAfterChangeMonthsYears?: () => void,
  onAfterChangeStartDate?: () => void
) => [
  {
    width: '100%',
    name: `${prefix}.companyName`,
  },
  {
    width: '100%',
    name: `${prefix}.phone`,
    label: 'Main Phone',
    component: PhoneInputField,
  },
  {
    width: '100%',
    name: `${prefix}.streetLine1`,
    label: 'Street',
  },
  {
    width: '50%',
    name: `${prefix}.unit`,
    label: 'Unit/Apt',
  },
  {
    width: '50%',
    name: `${prefix}.city`,
    label: 'City',
  },
  {
    width: '50%',
    name: `${prefix}.state`,
    label: 'State',
    options: statesList,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: `${prefix}.zip`,
    label: 'Zip',
    component: ZipCodeInputField,
  },
  {
    width: '100%',
    name: `${prefix}.jobTitle`,
    label: 'Job Title',
  },
  {
    width: '50%',
    name: `${prefix}.employmentType`,
    label: 'Type',
    options: EMPLOYMENT_TYPE_OPTIONS,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: `${prefix}.monthlyIncomeAmount`,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: `${prefix}.years`,
    label: 'Time (Years)',
    onAfterChange: onAfterChangeMonthsYears,
    component: NumberInputField,
  },
  {
    width: '50%',
    name: `${prefix}.months`,
    label: 'Time (Months)',
    onAfterChange: onAfterChangeMonthsYears,
    component: NumberInputField,
  },
  ...isCurrent ? [{
    width: '100%',
    name: `${prefix}.startDate`,
    label: 'Start Date',
    onAfterChange: onAfterChangeStartDate,
    component: SelectDateField,
  }] : [],
];


export const individualInfoConfigRight = [
  {
    width: '100%',
    name: 'email',
  },
  {
    width: '100%',
    name: 'dateOfBirth',
    label: 'DOB',
    component: SelectDateField,
  },
  {
    width: '100%',
    name: 'ssn',
    label: 'SSN',
    component: SSNInputField,
  },
];

export const individualInfoConfigLeftPart = [
  {
    width: '50%',
    name: 'firstName',
  },
  {
    width: '50%',
    name: 'middleInitial',
    label: 'Middle Name',
  },
  {
    width: '50%',
    name: 'lastName',
  },
  {
    width: '50%',
    name: 'suffix',
    component: SelectAutocompleteField,
    options: SUFFIX_OPTIONS,
  },
  {
    width: '100%',
    name: 'homePhone',
    component: PhoneInputField,
  },
  {
    width: '100%',
    name: 'cellPhone',
    component: PhoneInputField,
  },
  {
    width: '70%',
    name: 'workPhone',
    component: PhoneInputField,
  },
  {
    width: '30%',
    name: 'workPhoneExtension',
    removeThousandSeparator: true,
    component: NumberInputField,
    label: 'Phone Ext',
  },
  {
    width: '50%',
    name: 'maritalStatus',
    options: MARITAL_STATUS_OPTIONS,
    component: SelectAutocompleteField,
  },
  {
    width: '50%',
    name: 'militaryStatus',
    options: MILITARY_RANK_OPTIONS,
    component: SelectAutocompleteField,
  },
  {
    width: '100%',
    name: 'maidenName',
    label: 'Mother\'s Maiden Name',
  },
];